import React from "react";
import { colors } from "../styles/designSystem";
import { PipelineFlow, StepGrid, CycleFlow, DomainGrid } from "./diagrams/primitives";

/**
 * Diagrams for the Caterpillar case study.
 *
 * All four are compositions of the shared diagram primitives — the whole point of
 * extracting them. The only Caterpillar-specific thing here is the data.
 */

const ENTITY_MODEL = [
    { name: "Equipment", desc: "The machine the customer actually owns", color: colors.amber[400] },
    { name: "Systems", desc: "Hydraulics, engine, electrical — how a machine decomposes", color: colors.amber[400] },
    { name: "Maintenance Procedures", desc: "What has to be done, and when", color: colors.primary[500] },
    { name: "Steps", desc: "The procedure as a sequence, not a page", color: colors.primary[500] },
    { name: "Parts", desc: "What a step requires, linked to the catalog", color: colors.cyan[300] },
    { name: "Service", desc: "Where to go when the customer cannot do it alone", color: colors.emerald[500] },
];

export const EntityModel = () => (
    <PipelineFlow
        label="The Application Model That Replaced the Manual"
        stages={ENTITY_MODEL}
        caption="Once information is modelled as entities rather than pages, the same data can support several experiences instead of being locked inside a document."
    />
);

const MAINTENANCE_STEPS = [
    { name: "Select Equipment", color: colors.amber[400] },
    { name: "Find Maintenance Task", color: colors.amber[400] },
    { name: "View Procedure", color: colors.primary[500] },
    { name: "Follow Steps", color: colors.primary[500] },
    { name: "Identify Parts", color: colors.cyan[300] },
    { name: "Complete or Find Service", color: colors.emerald[500] },
];

export const MaintenanceFlow = () => (
    <StepGrid
        label="Maintenance as an Interactive Sequence"
        steps={MAINTENANCE_STEPS}
        columns={3}
        caption="Treating a procedure as application state and user actions, rather than as pages of content, is what lets the product guide someone through a job instead of handing them a manual."
    />
);

export const ConnectedWorkflow = () => (
    <CycleFlow
        label="Connected, Not Siloed"
        nodes={[
            { name: "Equipment", color: colors.amber[400] },
            { name: "Procedure", color: colors.primary[500] },
            { name: "Required Part", color: colors.cyan[300] },
            { name: "Parts Catalog", color: colors.cyan[300] },
            { name: "Dealer", color: colors.emerald[500] },
        ]}
        caption="Customers were rarely browsing the catalog. They were looking for something attached to a specific machine or a specific job, so the product connected those relationships instead of keeping separate digital silos."
    />
);

export const ReleaseLoop = () => (
    <CycleFlow
        label="Shipping Incrementally"
        nodes={[
            { name: "Build", color: colors.primary[500] },
            { name: "Test", color: colors.cyan[300] },
            { name: "Release", color: colors.emerald[500] },
            { name: "Measure", color: colors.amber[400] },
            { name: "Iterate", color: colors.coral[400] },
        ]}
        note="Findings changed navigation and architecture, not just visuals"
        noteColor={colors.coral[400]}
    />
);

const ROLE_DOMAINS = [
    { name: "Product Engineering", desc: "Features from problem definition through implementation, testing, and release.", color: colors.primary[500] },
    { name: "Frontend Development", desc: "Production application interfaces in React and JavaScript.", color: colors.cyan[300] },
    { name: "Application Architecture", desc: "Translating document-based information into reusable workflows and patterns.", color: colors.amber[400] },
    { name: "Technical Leadership", desc: "Led a five-person team and coordinated with distributed international engineering teams.", color: colors.emerald[500] },
    { name: "Research & Validation", desc: "Customer interviews, usability testing, A/B testing, and production feedback driving implementation.", color: colors.purple[400] },
    { name: "Reusable Patterns", desc: "Components able to support many equipment, maintenance, parts, and service workflows.", color: colors.coral[400] },
];

export const RoleDomains = () => (
    <DomainGrid label="What the Work Spanned" domains={ROLE_DOMAINS} />
);
